import {useTranslation} from "react-i18next";
import {useEffect, useLayoutEffect, useState} from "react";
import CustomPagination from "./CustomPagination";
import {VIEW_TYPE} from "../../enums/ViewType";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description General functions for use with <CustomTable>
 * @param UseState selectedList: List of selected IDs (required)
 * @param UseState setSelectedList: Set list of selected IDs (required)
 * @param Object data: List of elements to display in the table (required)
 * @param String view: Options: grid/list (optional)
 * @param String labelRowsPerPage: Text for the pagination label (optional)
 * @param Function getTbody: Function to get the tbody content (required)
 * @param Function getThead: Function to get the thead content (required)
 * @param Boolean fixedPagination: Indicates if the pagination is fixed (optional)
 * @param Boolean hasContainer: Indicates that the pagination this within a container waving in the fixedContainer es false (optional)
 * @param UseState setIsAllChecked: Set if checkbox in thead is checked (required)
 * @param UseState searchElement: Value give if you guide him value to look for him for the search field (optional)
 * @param Function getData               Fetches table data (simple search). Receives (page, filters). (optional)
 * @param Boolean modified               Flag indicating that data must be reloaded from page 0. (optional)
 * @param Function setModified           Setter for the `modified` flag. (optional)
 * @param Function getDataAdvancedSearch Fetches table data using advanced search filters. Receives (page). (optional)
 * @param Array finalArray               Final collection of results from advanced search. Used to trigger re-fetch. (optional)
 * @param String keyId                   Key that identifies a row in the selection, `id` by default. (optional)
 * @returns Object
 */

const TableFunction = ({
    keyId="id",
    selectedList,
    setSelectedList,
    data,
    view = VIEW_TYPE.LIST.name,
    labelRowsPerPage = '',
    getTbody,
    getThead,
    fixedPagination = true,
    hasContainer = false,
    setIsAllChecked,
    searchElement,

    /*** New values **/

    getData,
    modified,
    setModified,
    getDataAdvancedSearch,
    finalArray,
    dependencies=[]
}) => {
    const { t, i18n } = useTranslation();
    const [thead, setThead] = useState([]);
    const [tbody, setTBody] = useState([]);
    const [openMenu, setOpenMenu] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState('');

    const { pagination, page, setPage, rowsPerPage, setRowsPerPage, paginationModified, setPaginationModified } = CustomPagination({
        view,
        content: data,
        labelRowsPerPage,
        fixed: fixedPagination,
        hasContainer,
        searchElement,
        finalArray
    });

    /**
     * Function to indicate the row to be expanded.
     * @param row
     */
    const handleRowExpand = (row) => {
        if (row?.[keyId] !== expandedRowId) {
            setExpandedRowId(row?.[keyId]);
        }
    };

    const handleCheckboxChange = (e) => {
        const rowId = e.target[keyId];
        if(typeof selectedList !== "undefined"){
            let aux;
            if (e.target.checked) {
                aux = [...selectedList, rowId];
                setSelectedList(aux);
            } else {
                aux = selectedList.filter((id) => id !== rowId);
                setSelectedList(aux);
            }
            if(setIsAllChecked) setIsAllChecked(data?.content?.length === aux?.length);// Validating if exist setIsAllChecked and selectedList
        }else{
            if (e.target.checked) {
                setSelectedList((prev) => [...prev, rowId]);
            } else {
                setSelectedList((prev) => prev.filter((id) => id !== rowId));
            }
        }
    };

    const handleDeleteAll = (e) => {
        if (e.target.checked) {
            if (setIsAllChecked) setIsAllChecked(true);
            const selectAllIds = (data?.content || []).filter((item) => !item?.disabled).map((item) => item?.[keyId]);
            setSelectedList(selectAllIds);
        } else {
            if (setIsAllChecked) setIsAllChecked(false);
            setSelectedList([]);
        }
    };

    const handleMenuToggle = (menuId) => {
        setOpenMenu((prevMenu) => (prevMenu === menuId ? null : menuId));
    };

    const handleClose = () => {
        setOpenMenu(null);
    };

    /***
     * Description: Used for change language in table thead, render titles
     * **/
    i18n.on('languageChanged', lang => {
        if(setThead && getThead) setThead(getThead());
        if(setTBody && getTbody) setTBody(getTbody());
    });

    // Layout effect so the rows are rendered before the paint that follows the response, when the loader is already hidden //
    useLayoutEffect(() => {
        if(getTbody) setTBody(getTbody());
        if(getThead) setThead(getThead());
    }, [data?.content, ...dependencies]);

    /***
     * Description: Used for reset selected values when trigger simple search
     * **/
    useEffect(() => {
        if(setIsAllChecked) setIsAllChecked(false);
        if(setSelectedList) setSelectedList([]);
    }, [searchElement, finalArray]);

    /********New implementation************/
    /**
     * Initializes the table by requesting the first page of data on component mount.
     * This effect runs only once and ensures the initial dataset is loaded.
     */
    useEffect(() => {
        if(getData) getData(0, { rowsPerPage, searchElement });// page, criteriaList, rowsPerPage, searchElement
    }, []);

    /**
     * Handles pagination changes triggered by user interaction.
     *
     * When `paginationModified` is set to true, this effect determines whether to
     * request data through the advanced search function (`getDataAdvancedSearch`)
     * or the standard data loader (`getData`). After executing the request, it
     * resets the pagination modification flag asynchronously.
     */
    useEffect(() => {
        if(paginationModified){
            if (finalArray?.length > 0) {
                if (getDataAdvancedSearch) getDataAdvancedSearch(page, { rowsPerPage, searchElement });
            }else {
                if (getData) getData(page,{ rowsPerPage, searchElement }); // page, criteriaList, rowsPerPage, searchElement
            }

            setTimeout(() => setPaginationModified(false), 0);
            if(setIsAllChecked) setIsAllChecked(false);//Reset when pagination change
            if(setSelectedList) setSelectedList([]);//Reset when pagination change
        }
    }, [paginationModified]);

    /**
     * Triggers a new advanced search request when the `finalArray` changes.
     *
     * When `finalArray` contains data, it resets the page to 0 and re-executes the
     * advanced search using the provided handler.
     */
    useEffect(() => {
        if (finalArray?.length > 0) {
            if(getDataAdvancedSearch) getDataAdvancedSearch(0, { rowsPerPage, searchElement });
        }
    }, [finalArray]);

    /**
     * Re-fetches data when the modification flag is enabled.
     *
     * When `modified` is true, this effect loads data for the first page using `getData`
     * and then automatically resets the modification indicator.
     */
    useEffect(() => {
        if(modified){
            setPage(0);
            if(getData) getData(0, { rowsPerPage, searchElement });// page, criteriaList, rowsPerPage, searchElement
            if(setIsAllChecked) setIsAllChecked(false);//Reset when action is execute
            if(setSelectedList) setSelectedList([]);//Reset when action is execute
            if(setModified) setModified(false);
        }
    }, [modified]);

    return {
        t,
        thead,
        setThead,
        tbody,
        setTBody,
        openMenu,
        setOpenMenu,
        handleCheckboxChange,
        handleDeleteAll,
        handleMenuToggle,
        handleClose,
        pagination,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        handleRowExpand,
        expandedRowId,
        setExpandedRowId,
        paginationModified,
        setPaginationModified
    };
};
export default TableFunction;
